import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Link, X } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import type { Structure } from '../../types';

interface Props {
  structure: Structure;
  // Omit onClear when the structure was provided via route param — that
  // pairing isn't user-clearable in the create flow.
  onClear?: () => void;
}

/**
 * The composer's "Linked to Property" row. A hairline row with a small gray
 * label over the address, matching the sibling rows in Link & Options and
 * the mockup's metadata block — it was a filled Card, which read as the one
 * boxed element in a list of rows.
 */
export function LinkedStructureCard({ structure, onClear }: Props) {
  const { colors } = useTheme();
  return (
    <View style={[styles.row, { borderBottomColor: colors.border }]}>
      <Link size={16} color={colors.text} />
      <View style={styles.text}>
        <Text style={[styles.label, { color: colors.textTertiary }]}>Linked to Property</Text>
        <Text style={[styles.address, { color: colors.text }]} numberOfLines={1}>
          {structure.address || `${structure.city}, ${structure.state}`}
        </Text>
      </View>
      {onClear && (
        <TouchableOpacity
          onPress={onClear}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Unlink property">
          <X size={14} color={colors.textTertiary} />
        </TouchableOpacity>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingVertical: SPACING.smd,
    borderBottomWidth: 1,
  },
  text: {
    flex: 1,
    gap: 1,
  },
  label: {
    ...TYPOGRAPHY.metadata,
  },
  address: {
    ...TYPOGRAPHY.label,
  },
});
